import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, LockKey, LockKeyOpen, Buildings } from "@phosphor-icons/react";

export default function ResellerCompanies() {
  const { user } = useAuth();
  const brand = user?.reseller_brand_color || "#0F4C3A";
  const [items, setItems] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([api.get("/reseller/companies"), api.get("/reseller/metrics")]);
      setItems(c.data);
      setBalance(m.data.credit_balance);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "blocked" : "active";
    try {
      await api.patch(`/reseller/companies/${c.id}/status`, { status: next });
      toast.success(next === "blocked" ? "Empresa bloqueada" : "Empresa reativada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-8">
        <div>
          <p className="text-xs font-semibold tracking-[0.3em] uppercase" style={{ color: brand }}>Rede</p>
          <h1 className="text-4xl font-black tracking-tighter">Minhas Empresas</h1>
          <p className="text-sm text-[#5C5C59] mt-2">Sub-clientes conectados na sua conta white-label.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right px-3 py-2 border border-[#E5E5E0] bg-white">
            <div className="text-[9px] uppercase tracking-wider text-[#5C5C59]">Créditos</div>
            <div className="text-xl font-black font-mono" style={{ color: balance < 5 ? "#DC2626" : brand }}>
              {balance ?? "—"}
            </div>
          </div>
          <Button onClick={() => setOpenCreate(true)} data-testid="create-company-btn"
            className="rounded-none text-white" style={{ background: brand }}>
            <Plus size={14} weight="bold" className="mr-2" /> Nova empresa
          </Button>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E0] shadow-brutal">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E5E0] text-[10px] uppercase tracking-wider text-[#5C5C59]">
              <th className="text-left px-4 py-3">Empresa</th>
              <th className="text-left px-4 py-3">E-mail dono</th>
              <th className="text-left px-4 py-3">Plano</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Criada em</th>
              <th className="text-right px-4 py-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-6 text-[#5C5C59]">Carregando…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={6} className="text-center py-10 text-[#5C5C59]">
                <Buildings size={28} className="mx-auto mb-2 opacity-40" />
                Nenhuma empresa criada ainda.
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} data-testid={`company-row-${c.id}`} className="border-b border-[#F0F0EC] hover:bg-[#F7F7F5]">
                <td className="px-4 py-3 font-semibold">{c.name}</td>
                <td className="px-4 py-3 text-[#5C5C59] font-mono text-xs">{c.owner_email}</td>
                <td className="px-4 py-3 text-xs">{c.plan_name || "—"}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${c.status === "active" ? "bg-[#EDF3F0] text-[#0F4C3A]" : "bg-[#FEE2E2] text-[#DC2626]"}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-xs text-[#5C5C59]">{new Date(c.created_at).toLocaleDateString("pt-BR")}</td>
                <td className="px-4 py-3 text-right">
                  <button data-testid={`toggle-company-${c.id}`} onClick={() => toggleStatus(c)} className="p-2 border border-[#E5E5E0] hover:bg-[#F7F7F5]" title={c.status === "active" ? "Bloquear" : "Desbloquear"}>
                    {c.status === "active" ? <LockKey size={14} weight="bold" /> : <LockKeyOpen size={14} weight="bold" />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CreateCompanyDialog open={openCreate} onOpenChange={setOpenCreate} onDone={load} brand={brand} />
    </div>
  );
}

function CreateCompanyDialog({ open, onOpenChange, onDone, brand }) {
  const [form, setForm] = useState({ name: "", owner_email: "", owner_name: "", owner_password: "", plan_id: "", is_reseller_plan: false });
  const [masterPlans, setMasterPlans] = useState([]);
  const [resellerPlans, setResellerPlans] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      (async () => {
        try {
          const [mp, rp] = await Promise.all([api.get("/plans"), api.get("/reseller/plans")]);
          setMasterPlans(mp.data);
          setResellerPlans(rp.data);
        } catch (e) { toast.error(formatApiError(e)); }
      })();
    }
  }, [open]);

  const submit = async () => {
    if (!form.plan_id) return toast.error("Escolha um plano");
    setLoading(true);
    try {
      await api.post("/reseller/companies", form);
      toast.success("Empresa criada");
      onOpenChange(false);
      setForm({ name: "", owner_email: "", owner_name: "", owner_password: "", plan_id: "", is_reseller_plan: false });
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-none border-[#E5E5E0] max-w-lg" data-testid="create-company-dialog">
        <DialogHeader><DialogTitle>Nova empresa</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Nome da empresa</Label>
            <Input data-testid="company-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">Nome do dono</Label>
              <Input data-testid="company-owner-name-input" value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-none mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold tracking-wider">E-mail</Label>
              <Input data-testid="company-owner-email-input" type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="rounded-none mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Senha</Label>
            <Input data-testid="company-owner-password-input" type="password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} className="rounded-none mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase font-semibold tracking-wider">Plano</Label>
            <Select
              value={form.is_reseller_plan ? `r:${form.plan_id}` : (form.plan_id ? `m:${form.plan_id}` : "")}
              onValueChange={(val) => {
                const [type, id] = val.split(":");
                setForm({ ...form, plan_id: id, is_reseller_plan: type === "r" });
              }}
            >
              <SelectTrigger className="rounded-none mt-1" data-testid="company-plan-select">
                <SelectValue placeholder="Selecione um plano" />
              </SelectTrigger>
              <SelectContent>
                {masterPlans.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-[#5C5C59] font-bold">Planos padrão</div>
                    {masterPlans.map((p) => (
                      <SelectItem key={p.id} value={`m:${p.id}`}>{p.name} — R$ {p.price.toFixed(2)} (1 crédito)</SelectItem>
                    ))}
                  </>
                )}
                {resellerPlans.length > 0 && (
                  <>
                    <div className="px-2 py-1 text-[9px] uppercase tracking-wider text-[#5C5C59] font-bold border-t border-[#E5E5E0] mt-1">Seus planos</div>
                    {resellerPlans.map((p) => (
                      <SelectItem key={p.id} value={`r:${p.id}`}>{p.name} — R$ {p.price.toFixed(2)} ({p.credit_cost} créd.)</SelectItem>
                    ))}
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-none">Cancelar</Button>
          <Button data-testid="submit-create-company" onClick={submit} disabled={loading} className="rounded-none text-white" style={{ background: brand }}>
            {loading ? "Criando…" : "Criar (débito de créditos)"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
